import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Package, CheckCircle2, Truck, Home } from "lucide-react";
import api, { img, money } from "../lib/api";
import { toast } from "sonner";

const STEPS = ["Order Confirmed", "Payment Confirmed", "Processing", "Packed", "Shipped", "Out for Delivery", "Delivered"];

export default function TrackOrder() {
  const [sp] = useSearchParams();
  const [num, setNum] = useState(sp.get("order") || "");
  const [order, setOrder] = useState(null);

  const fetchOrder = async (n) => {
    try { const r = await api.get(`/orders/track/${n}`); setOrder(r.data); }
    catch { toast.error("Order not found"); setOrder(null); }
  };
  useEffect(() => { if (sp.get("order")) fetchOrder(sp.get("order")); }, []);

  const statusMap = { pending: 0, processing: 2, packed: 3, shipped: 4, out_for_delivery: 5, delivered: 6 };
  const currentStep = order ? Math.max(order.payment.status === "paid" ? 1 : 0, statusMap[order.fulfillment_status] ?? 0) : 0;

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-10 py-16">
      <h1 className="serif text-5xl mb-3">Track Your Order</h1>
      <p className="text-forest/60 mb-8">Enter your order number to see the latest status.</p>
      <div className="flex gap-3 mb-12">
        <input value={num} onChange={(e) => setNum(e.target.value)} placeholder="e.g. IAH2606XXXX" data-testid="track-input"
          className="input-line flex-1" />
        <button onClick={() => fetchOrder(num)} className="btn-primary" data-testid="track-submit">Track</button>
      </div>

      {order && (
        <div className="space-y-10" data-testid="track-result">
          <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-sage/30">
            <div><div className="eyebrow text-forest/50">Order</div><div className="serif text-2xl">{order.order_number}</div></div>
            <div><div className="eyebrow text-forest/50">Placed</div><div className="text-sm">{new Date(order.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</div></div>
            <div><div className="eyebrow text-forest/50">Total</div><div className="serif text-2xl">{money(order.total)}</div></div>
          </div>

          {/* timeline */}
          <div className="relative">
            {STEPS.map((s, i) => {
              const done = i <= currentStep;
              return (
                <div key={s} className="flex gap-4 pb-8 last:pb-0 relative">
                  {i < STEPS.length - 1 && <div className={`absolute left-[11px] top-6 bottom-0 w-px ${i < currentStep ? "bg-forest" : "bg-sage/40"}`} />}
                  <div className={`h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${done ? "bg-forest text-ivory" : "bg-sage/30 text-forest/40"}`}>
                    {done ? <CheckCircle2 size={14} /> : <div className="h-2 w-2 rounded-full bg-current" />}
                  </div>
                  <div className={done ? "text-forest" : "text-forest/40"}>
                    <div className="text-sm font-medium">{s}</div>
                    {i === currentStep && order.shipment?.awb && <div className="text-xs text-forest/55 mt-1">AWB {order.shipment.awb} · {order.shipment.courier}</div>}
                  </div>
                </div>
              );
            })}
          </div>

          {order.shipment?.awb && (
            <div className="bg-beige/40 p-6 flex flex-wrap gap-6 text-sm">
              <div><div className="eyebrow text-forest/50">Courier</div>{order.shipment.courier}</div>
              <div><div className="eyebrow text-forest/50">AWB</div>{order.shipment.awb}</div>
              {order.shipment.status && <div><div className="eyebrow text-forest/50">Status</div>{order.shipment.status}</div>}
            </div>
          )}

          <div className="divide-y divide-sage/20 border-t border-sage/30 pt-2">
            {order.items.map((it) => (
              <div key={it.slug} className="flex gap-4 py-4">
                <img src={img(it.image)} alt="" className="h-16 w-14 object-cover bg-beige/40" />
                <div className="flex-1"><div className="serif text-lg">{it.name}</div><div className="text-xs text-forest/50">Qty {it.quantity}</div></div>
                <span className="text-sm">{money(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
